<template>
  <div class="chart-outer">
    <div class="bar-chart" :class="{ rotate: chartOptions.rotate === 'yes' }">
      <svg
        :width="chartSizeStyle"
        :height="chartSizeStyle"
        :viewBox="chartOptions.viewBox"
      >
        <!-- Full Circle Paths for Each Item (Background Circle) -->
        <circle
          v-for="index in chartOptions.circles"
          :key="'bg-' + index"
          :r="chartOptions.circleRadius - (index - 1) * chartOptions.gap"
          :cx="circleCenter"
          :cy="circleCenter"
          :stroke="chartOptions.backgroundCircleColor"
          :stroke-width="chartOptions.circleWidth - 0.35"
          fill="none"
        />
        <!-- Dynamic Radial Bars -->
        <circle
          v-for="(item, index) in chartData"
          :key="index"
          :r="chartOptions.circleRadius - index * chartOptions.gap"
          :cx="circleCenter"
          :cy="circleCenter"
          :stroke="item.color || '#FF5733'"
          :stroke-width="chartOptions.circleWidth"
          :stroke-dasharray="
            getStrokeDasharray(
              item.percentage,
              chartOptions.circleRadius - index * chartOptions.gap
            )
          "
          fill="none"
          stroke-linecap="round"
        />
      </svg>
    </div>
    <ChartLegend v-if="showLegend" :data="props.data" fontSize="medium" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { IChartDataItem } from '@/components/common/common'
import ChartLegend from '@/components/common/ChartLegend.vue'

interface IProps {
  data: IChartDataItem[]
  showLegend?: boolean
  chartOptions?: {
    chartSize?: number | string
    circleRadius?: number
    circleWidth?: number
    gap?: number
    maxPercent?: number
    viewBox?: string
    rotate?: string
    backgroundCircleColor?: string
    circles?: number
  }
  logoComponent?: any
}

const props = defineProps<IProps>()

const circleCenter = 50 // Center of the circle (50% of chartSize)

// Use default values for chartOptions if not provided
const chartOptions = computed(() => ({
  chartSize: props.chartOptions?.chartSize || '14rem',
  circleRadius: props.chartOptions?.circleRadius || 42.5,
  circleWidth: props.chartOptions?.circleWidth || 3.5,
  gap: props.chartOptions?.gap || 8,
  maxPercent: props.chartOptions?.maxPercent || 150,
  viewBox: props.chartOptions?.viewBox || '0 0 100 100',
  rotate: props.chartOptions?.rotate || 'yes',
  backgroundCircleColor: props.chartOptions?.backgroundCircleColor || '#f1f1f1',
  circles: props.chartOptions?.circles || 5
}))

const chartSizeStyle = computed(() => {
  const size = chartOptions.value.chartSize
  return typeof size === 'number' ? `${size}px` : size
})

// Data for the radial bars
const chartData = computed(() => {
  return props.data.map((item) => ({
    label: item.label,
    percentage: item.percentage || 0,
    color: item.color,
    value: item.value || 0
  }))
})

// Function to calculate the stroke-dasharray based on percentage
const getStrokeDasharray = (percentage: number, radius: number) => {
  const circumference = 2 * Math.PI * radius
  return `${(percentage / chartOptions.value.maxPercent) * circumference} ${circumference}`
}
</script>

<style scoped>
.chart-outer {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative; /* Needed to position logo at center */
}

.rotate {
  transform: rotate(-90deg); /* Rotate to make the chart start from top */
}
</style>
